import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const QuizView = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchQuiz();
  }, [moduleId]);

  const fetchQuiz = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/quiz/modules/${moduleId}/take`);
      setQuiz(res.data);
      // If already passed show result
      if (res.data.bestAttempt?.passed) {
        setResult({
          score: res.data.bestAttempt.score,
          passed: true,
          correct: null,
          total: res.data.questions.length,
          passingScore: res.data.module.passing_score || 80,
          alreadyPassed: true,
        });
        setSubmitted(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (questionId, answerId) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerId }));
  };

  const handleSubmit = async () => {
    const unanswered = quiz.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      setError(`Please answer all questions. ${unanswered.length} remaining.`);
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/quiz/modules/${moduleId}/submit`,
        { answers }
      );
      setResult(res.data);
      setSubmitted(true);
    } catch (err) {
      setError('Error submitting quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setSubmitted(false);
    setResult(null);
    setError('');
    fetchQuiz();
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading quiz...</div>
      </div>
    );
  }

  if (!quiz || quiz.questions.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>No Quiz Available</h2>
          <p style={styles.subtitle}>This module doesn't have a quiz yet.</p>
          <button style={styles.backBtn} onClick={() => navigate(-1)}>
            ← Back to Module
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <button style={styles.backBtn} onClick={() => navigate(-1)}>
          ← Back to Module
        </button>
      </div>

      <div style={styles.main}>
        <div style={styles.quizHeader}>
          <h2 style={styles.quizTitle}>📝 Module Quiz</h2>
          <p style={styles.quizSubtitle}>{quiz.module?.title}</p>
          <div style={styles.quizMeta}>
            <span style={styles.metaItem}>
              {quiz.questions.length} questions
            </span>
            <span style={styles.metaDivider}>·</span>
            <span style={styles.metaItem}>
              Passing score: {quiz.module?.passing_score || 80}%
            </span>
            {quiz.attemptCount > 0 && (
              <>
                <span style={styles.metaDivider}>·</span>
                <span style={styles.metaItem}>
                  Attempts: {quiz.attemptCount}
                </span>
              </>
            )}
            {quiz.bestAttempt && (
              <>
                <span style={styles.metaDivider}>·</span>
                <span style={styles.metaItem}>
                  Best score: {quiz.bestAttempt.score}%
                </span>
              </>
            )}
          </div>
        </div>

        {/* Results Screen */}
        {submitted && result && (
          <div style={styles.resultCard}>
            <div style={styles.resultIcon}>
              {result.passed ? '🎉' : '📚'}
            </div>
            <h3 style={{
              ...styles.resultTitle,
              color: result.passed ? '#1E8449' : '#C49A28'
            }}>
              {result.alreadyPassed
                ? 'Quiz Already Passed!'
                : result.passed
                ? 'Quiz Passed!'
                : 'Not Quite — Try Again'}
            </h3>
            <div style={styles.scoreDisplay}>
              <span style={{
                ...styles.scoreNumber,
                color: result.passed ? '#1E8449' : '#C49A28'
              }}>
                {result.score}%
              </span>
              <span style={styles.scoreLabel}>Your Score</span>
            </div>
            {!result.alreadyPassed && result.correct !== null && (
              <p style={styles.resultDetail}>
                {result.correct} out of {result.total} correct
              </p>
            )}
            <p style={styles.passingNote}>
              Passing score: {result.passingScore}%
            </p>

            {result.passed && (
              <div style={styles.passedActions}>
                <p style={styles.passedText}>
                  ✅ This module is now unlocked. You can proceed to the next module.
                </p>
                <button
                  style={styles.continueBtn}
                  onClick={() => navigate(-1)}
                >
                  ← Return to Module
                </button>
              </div>
            )}

            {!result.passed && (
              <div style={styles.failedActions}>
                <p style={styles.failedText}>
                  You need {result.passingScore}% to pass. Review the material and try again.
                </p>
                <button style={styles.retakeBtn} onClick={handleRetake}>
                  Retake Quiz
                </button>
              </div>
            )}
          </div>
        )}

        {/* Quiz Questions */}
        {!submitted && (
          <div style={styles.questionsContainer}>
            {quiz.questions.map((question, index) => (
              <div key={question.id} style={styles.questionCard}>
                <div style={styles.questionHeader}>
                  <span style={styles.questionNum}>
                    Question {index + 1} of {quiz.questions.length}
                  </span>
                </div>
                <p style={styles.questionText}>{question.question_text}</p>

                <div style={styles.answersGrid}>
                  {question.answers && question.answers.map(answer => (
                    <button
                      key={answer.id}
                      style={{
                        ...styles.answerBtn,
                        ...(answers[question.id] === answer.id ?
                          styles.answerBtnSelected : {})
                      }}
                      onClick={() => handleSelectAnswer(question.id, answer.id)}
                    >
                      <span style={{
                        ...styles.answerCircle,
                        ...(answers[question.id] === answer.id ?
                          styles.answerCircleSelected : {})
                      }}>
                        {answers[question.id] === answer.id ? '✓' : ''}
                      </span>
                      {answer.answer_text}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {error && <div style={styles.errorBox}>{error}</div>}

            <div style={styles.submitRow}>
              <p style={styles.progressText}>
                {Object.keys(answers).length} of {quiz.questions.length} answered
              </p>
              <button
                style={submitting ? styles.submitBtnDisabled : styles.submitBtn}
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Quiz'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#1A2744', color: '#ffffff' },
  loading: { color: '#ffffff60', fontSize: '16px', textAlign: 'center', marginTop: '100px' },
  header: { backgroundColor: '#0f1a33', borderBottom: '1px solid #C49A28', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { color: '#C49A28', fontSize: '24px', fontWeight: '800', margin: '0', letterSpacing: '2px' },
  logoText: { color: '#ffffff80', fontSize: '14px', letterSpacing: '1px' },
  backBtn: { backgroundColor: 'transparent', border: '1px solid #C49A28', color: '#C49A28', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  main: { maxWidth: '800px', margin: '0 auto', padding: '32px' },
  quizHeader: { marginBottom: '32px', textAlign: 'center' },
  quizTitle: { color: '#C49A28', fontSize: '28px', fontWeight: '700', margin: '0 0 8px 0' },
  quizSubtitle: { color: '#ffffff80', fontSize: '16px', margin: '0 0 12px 0' },
  quizMeta: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' },
  metaItem: { color: '#ffffff60', fontSize: '13px' },
  metaDivider: { color: '#ffffff30', fontSize: '13px' },
  resultCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '16px', padding: '48px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
  resultIcon: { fontSize: '64px' },
  resultTitle: { fontSize: '24px', fontWeight: '700', margin: '0' },
  scoreDisplay: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' },
  scoreNumber: { fontSize: '56px', fontWeight: '800', lineHeight: '1' },
  scoreLabel: { color: '#ffffff50', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' },
  resultDetail: { color: '#ffffff80', fontSize: '15px', margin: '0' },
  passingNote: { color: '#ffffff40', fontSize: '13px', margin: '0' },
  passedActions: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginTop: '8px' },
  passedText: { color: '#1E8449', fontSize: '14px', margin: '0' },
  continueBtn: { backgroundColor: '#1E8449', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '12px 28px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  failedActions: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginTop: '8px' },
  failedText: { color: '#ffffff60', fontSize: '14px', margin: '0' },
  retakeBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '8px', padding: '12px 28px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  questionsContainer: { display: 'flex', flexDirection: 'column', gap: '24px' },
  questionCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '12px', padding: '28px' },
  questionHeader: { marginBottom: '12px' },
  questionNum: { color: '#C49A28', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' },
  questionText: { color: '#ffffff', fontSize: '17px', fontWeight: '600', margin: '0 0 20px 0', lineHeight: '1.5' },
  answersGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  answerBtn: { backgroundColor: '#1A2744', border: '1px solid #ffffff15', borderRadius: '8px', padding: '14px 18px', color: '#ffffffcc', fontSize: '14px', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.15s' },
  answerBtnSelected: { backgroundColor: '#C49A2820', border: '1px solid #C49A28', color: '#ffffff' },
  answerCircle: { width: '22px', height: '22px', borderRadius: '50%', border: '2px solid #ffffff30', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' },
  answerCircleSelected: { backgroundColor: '#C49A28', border: '2px solid #C49A28', color: '#1A2744' },
  errorBox: { backgroundColor: '#ff000015', border: '1px solid #ff000040', borderRadius: '8px', padding: '12px 16px', color: '#ff6666', fontSize: '13px' },
  submitRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' },
  progressText: { color: '#ffffff50', fontSize: '13px', margin: '0' },
  submitBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '8px', padding: '12px 32px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
  submitBtnDisabled: { backgroundColor: '#C49A2840', color: '#1A274460', border: 'none', borderRadius: '8px', padding: '12px 32px', fontSize: '15px', fontWeight: '700', cursor: 'not-allowed' },
  card: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '16px', padding: '48px', textAlign: 'center', maxWidth: '500px', margin: '80px auto', display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' },
  title: { color: '#C49A28', fontSize: '22px', fontWeight: '700', margin: '0' },
  subtitle: { color: '#ffffff60', fontSize: '14px', margin: '0' },
};

export default QuizView;